import React, { useEffect, useState } from 'react';
import CardContent from '@material-ui/core/CardContent';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import Card from '@material-ui/core/Card';
import useTheme from '@material-ui/core/styles/useTheme';
import { FormattedDate } from 'react-intl';
import getUsages from '@afosto/utils-react/getUsages';
import formatBytes from '@afosto/utils/string/formatBytes';
import useIntl from '@afosto/utils-react/intl/useIntl';
import DatePicker from '@afosto/app-components-react/DatePicker';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import translations from './translations';
import CardHeader from '@material-ui/core/CardHeader';

const renderTrafficTooltip = args => {
  const { payload } = args;
  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="textSecondary">
          {payload?.[0]?.payload?.date && (
            <FormattedDate
              value={new Date(payload[0].payload.date)}
              weekday="long"
              day="numeric"
              month="long"
            />
          )}
        </Typography>
        <Typography variant="body1">
          {formatBytes((payload?.[0]?.payload?.quantity ?? 0) * 1048576)}
        </Typography>
      </CardContent>
    </Card>
  );
};

const Traffic30DaysSection = () => {
  const dateRangeDefaultStart = new Date();
  dateRangeDefaultStart.setDate(dateRangeDefaultStart.getDate() - 30);
  const dateRangeDefaultEnd = new Date();

  const intl = useIntl();
  const [trafficPerDay, setTrafficPerDay] = useState(null);
  const [dateRange, setDateRange] = useState([dateRangeDefaultStart, dateRangeDefaultEnd]);
  const [isLoadingChart, setIsLoadingChart] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    let isMounted = true;

    const getTrafficPerDay = async () => {
      try {
        setIsLoadingChart(true);
        const data = await getUsages({
          unit: 'mb',
          code: 'qcq',
          startAt: dateRange[0],
          endAt: dateRange[1],
          interval: '1d',
          as: 'sum',
        });
        if (isMounted) {
          setTrafficPerDay(data);
        }
      } catch (error) {
        if (isMounted) {
          setTrafficPerDay([]);
        }
      } finally {
        if (isMounted) {
          setIsLoadingChart(false);
        }
      }
    };

    getTrafficPerDay();

    return () => {
      isMounted = false;
    };
  }, [dateRange]);

  const formatXAxisTick = tick =>
    intl.formatDate(new Date(tick), { day: 'numeric', month: 'short' });

  const handleChangeEndDate = newValue => {
    const newValueMinus30 = new Date(newValue);
    newValueMinus30.setDate(newValueMinus30.getDate() - 30);
    setDateRange([newValueMinus30, newValue]);
  };

  const handleChangeStartDate = newValue => {
    const newValuePlus30 = new Date(newValue);
    newValuePlus30.setDate(newValuePlus30.getDate() + 30);
    setDateRange([newValue, newValuePlus30]);
  };
  return (
    <Card>
      <CardHeader
        title={intl.formatMessage(translations.title)}
        titleTypographyProps={{ variant: 'h6' }}
        action={
          <div>
            <Grid container spacing={1}>
              <Grid item>
                <DatePicker
                  name="start_date"
                  margin="dense"
                  value={dateRange[0] ?? null}
                  onChange={handleChangeStartDate}
                  label={intl.formatMessage(translations.fields.from)}
                  maxDate={dateRangeDefaultStart}
                />
              </Grid>
              <Grid item>
                <DatePicker
                  name="end_date"
                  disableFuture
                  margin="dense"
                  value={dateRange[1] ?? null}
                  onChange={handleChangeEndDate}
                  label={intl.formatMessage(translations.fields.to)}
                />
              </Grid>
            </Grid>
          </div>
        }
      />
      <CardContent>
        <LoadingWrapper isLoading={isLoadingChart}>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart
              width={500}
              height={300}
              data={trafficPerDay?.buckets ?? []}
              margin={{
                top: 5,
                right: 30,
                left: 20,
                bottom: 5,
              }}
            >
              <CartesianGrid vertical={false} stroke="rgba(0, 0, 0, 0.05)" />
              <YAxis tickFormatter={tick => formatBytes(tick * 1048576)} axisLine={false} />
              <XAxis dataKey="date" interval={6} tickFormatter={formatXAxisTick} />
              <Tooltip content={renderTrafficTooltip} />
              <Bar type="monotone" dataKey="quantity" fill={theme.palette.primary.main} />
            </BarChart>
          </ResponsiveContainer>
        </LoadingWrapper>
      </CardContent>
    </Card>
  );
};

export default Traffic30DaysSection;
